import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Moon, Sun, Volume2, VolumeX, Globe } from 'lucide-react';
import { getDatabaseScope, supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { debug } from '../lib/utils';
import type { DailyPuzzleGame } from '../lib/dailyPuzzle';
import { read2048Record } from '../lib/game2048Records';

interface GameStats {
  easy: number;
  medium: number;
  hard: number;
  expert: number;
}

// Add these utility functions at the top
const SUDOKU_DIFFICULTY_KEY = 'sudokuDifficultyPreference';
const FIFTEEN_PUZZLE_DIFFICULTY_KEY = 'fifteenPuzzleDifficultyPreference';

const saveUserPreferredDifficulty = (game: Exclude<DailyPuzzleGame, '2048'>, difficulty: number): void => {
  const keys: Record<Exclude<DailyPuzzleGame, '2048'>, string> = {
    sudoku: SUDOKU_DIFFICULTY_KEY,
    '15puzzle': FIFTEEN_PUZZLE_DIFFICULTY_KEY
  };
  const key = keys[game];
  localStorage.setItem(key, difficulty.toString());
};

// Create a DifficultySelector component that uses radio buttons
const DifficultySelector = ({
  game,
  currentDifficulty,
  onChange
}: {
  game: Exclude<DailyPuzzleGame, '2048'>;
  currentDifficulty: number;
  onChange: (difficulty: number) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id={`${game}-easy`}
          name={`${game}-difficulty`}
          value="1"
          checked={currentDifficulty === 1}
          onChange={() => onChange(1)}
          className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
        />
        <label htmlFor={`${game}-easy`} className="inline-flex min-h-11 items-center text-gray-700 dark:text-gray-300">
          {t('easy')}
        </label>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id={`${game}-medium`}
          name={`${game}-difficulty`}
          value="2"
          checked={currentDifficulty === 2}
          onChange={() => onChange(2)}
          className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
        />
        <label htmlFor={`${game}-medium`} className="inline-flex min-h-11 items-center text-gray-700 dark:text-gray-300">
          {t('medium')}
        </label>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id={`${game}-hard`}
          name={`${game}-difficulty`}
          value="3"
          checked={currentDifficulty === 3}
          onChange={() => onChange(3)}
          className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
        />
        <label htmlFor={`${game}-hard`} className="inline-flex min-h-11 items-center text-gray-700 dark:text-gray-300">
          {t('hard')}
        </label>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id={`${game}-expert`}
          name={`${game}-difficulty`}
          value="4"
          checked={currentDifficulty === 4}
          onChange={() => onChange(4)}
          className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
        />
        <label htmlFor={`${game}-expert`} className="inline-flex min-h-11 items-center text-gray-700 dark:text-gray-300">
          {t('expert')}
        </label>
      </div>
    </div>
  );
};

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [record2048, setRecord2048] = useState<{ user: string | null; value: number | null } | null>(null);
  const [recordError, setRecordError] = useState(false);
  const [recordRetry, setRecordRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRecordError(false);
    getDatabaseScope().then(scope => read2048Record(scope.client)).then(value => {
      if (!cancelled) {
        setRecord2048({ user: currentUser?.uid ?? null, value });
      }
    }).catch(error => {
      if (!cancelled) {
        setRecordError(true);
        debug('Error reading Profile 2048 record:', error);
      }
    });
    return () => { cancelled = true; };
  }, [currentUser, recordRetry]);

  // Preferences
  const [soundEnabled, setSoundEnabled] = useLocalStorage<boolean>('sound-enabled', true);
  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains('dark');
  });

  // Game preferences
  const [sudokuDifficulty, setSudokuDifficulty] = useLocalStorage<number>('sudokuDifficultyPreference', 3);
  const [fifteenPuzzleDifficulty, setFifteenPuzzleDifficulty] = useLocalStorage<number>('fifteenPuzzleDifficultyPreference', 3);

  // Game statistics (now from database)
  const [sudokuStats, setSudokuStats] = useState<GameStats>({
    easy: 0,
    medium: 0,
    hard: 0,
    expert: 0
  });

  const [fifteenPuzzleStats, setFifteenPuzzleStats] = useState<GameStats>({
    easy: 0,
    medium: 0,
    hard: 0,
    expert: 0
  });

  const [game2048Stats, setGame2048Stats] = useState<GameStats>({
    easy: 0,
    medium: 0,
    hard: 0,
    expert: 0
  });

  // Add state for completed days stats
  const [completedDays, setCompletedDays] = useState({
    total: 0,
    streak: 0,
    lastCompleted: '',
  });

  // Process Sudoku stats - Replace any[] with a proper interface
  const processSudokuStats = useCallback((data: {difficulty: number}[]) => {
    const stats: GameStats = {
      easy: 0,
      medium: 0,
      hard: 0,
      expert: 0
    };

    data.forEach(item => {
      if (item.difficulty === 1) stats.easy++;
      else if (item.difficulty === 2) stats.medium++;
      else if (item.difficulty === 3) stats.hard++;
      else if (item.difficulty === 4) stats.expert++;
    });

    setSudokuStats(stats);
  }, []);

  // Process 15 Puzzle stats - Replace any[] with a proper interface
  const processPuzzleStats = useCallback((data: {difficulty: number}[]) => {
    const stats: GameStats = {
      easy: 0,
      medium: 0,
      hard: 0,
      expert: 0
    };

    data.forEach(item => {
      if (item.difficulty === 1) stats.easy++;
      else if (item.difficulty === 2) stats.medium++;
      else if (item.difficulty === 3) stats.hard++;
      else if (item.difficulty === 4) stats.expert++;
    });

    setFifteenPuzzleStats(stats);
  }, []);

  const process2048Stats = useCallback((data: {difficulty: number}[]) => {
    const stats: GameStats = {
      easy: 0,
      medium: 0,
      hard: 0,
      expert: 0
    };

    data.forEach(item => {
      if (item.difficulty === 1) stats.easy++;
      else if (item.difficulty === 2) stats.medium++;
      else if (item.difficulty === 3) stats.hard++;
      else if (item.difficulty === 4) stats.expert++;
    });

    setGame2048Stats(stats);
  }, []);

  const fetchCurrentPrincipalStats = useCallback(async () => {
    // Fetch Sudoku stats
    const { data: sudokuData, error: sudokuError } = await supabase
      .from('games')
      .select('difficulty')
      .eq('game_type', 'sudoku');

    if (sudokuError) {
      throw sudokuError;
    } else {
      processSudokuStats(sudokuData || []);
    }

    // Fetch 15 Puzzle stats
    const { data: puzzleData, error: puzzleError } = await supabase
      .from('games')
      .select('difficulty')
      .eq('game_type', '15puzzle');

    if (puzzleError) {
      throw puzzleError;
    } else {
      processPuzzleStats(puzzleData || []);
    }

    const { data: game2048Data, error: game2048Error } = await supabase
      .from('games')
      .select('difficulty')
      .eq('game_type', '2048');

    if (game2048Error) {
      throw game2048Error;
    } else {
      process2048Stats(game2048Data || []);
    }
  }, [processSudokuStats, processPuzzleStats, process2048Stats]);

  const fetchCompletedDaysStats = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('puzzle_date, game_type');

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return;
      }

      // Group by date
      const dateMap = new Map();
      data.forEach(item => {
        const date = item.puzzle_date;
        if (!dateMap.has(date)) {
          dateMap.set(date, new Set());
        }
        dateMap.get(date).add(item.game_type);
      });

      // Count days with all active game types
      let completedTotal = 0;
      let currentStreak = 0;
      let lastCompleted = '';
      const completedDates = [];

      // Sort dates in descending order (newest first)
      const sortedDates = Array.from(dateMap.keys()).sort((a, b) =>
        new Date(b).getTime() - new Date(a).getTime()
      );

      for (const date of sortedDates) {
        const games = dateMap.get(date);
        if (games.has('sudoku') && games.has('15puzzle') && games.has('2048')) {
          completedTotal++;
          completedDates.push(date);

          if (lastCompleted === '') {
            lastCompleted = date;
          }
        }
      }

      // Calculate streak
      if (completedDates.length > 0) {
        currentStreak = 1; // Start with the most recent day
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const latestDate = new Date(completedDates[0]);
        latestDate.setHours(0, 0, 0, 0);

        // Check if the latest completed day is today or yesterday
        const daysDiff = Math.floor((today.getTime() - latestDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff > 1) {
          // Streak is broken if latest completion is more than 1 day ago
          currentStreak = 0;
        } else {
          // Check consecutive days backwards from the latest day
          for (let i = 1; i < completedDates.length; i++) {
            const currentDate = new Date(completedDates[i]);
            const prevDate = new Date(completedDates[i-1]);

            const diffDays = Math.floor((prevDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
              currentStreak++;
            } else {
              break;
            }
          }
        }
      }

      setCompletedDays({
        total: completedTotal,
        streak: currentStreak,
        lastCompleted
      });

    } catch (error) {
      debug('Error processing completed days:', error);
    }
  }, []);

  // Update the useEffect with proper dependencies
  useEffect(() => {
    const fetchGameStats = async () => {
      try {
        await fetchCurrentPrincipalStats();
        await fetchCompletedDaysStats();
      } catch (error) {
        debug('Error fetching game statistics:', error);
      }
    };

    fetchGameStats();
  }, [currentUser, fetchCurrentPrincipalStats, fetchCompletedDaysStats]);

  // Add a custom event listener for settings changes
  React.useEffect(() => {
    // Function to handle settings changes from Header
    const handleSettingsChange = (event: CustomEvent) => {
      const { key, value } = event.detail;

      if (key === 'theme') {
        setIsDark(value === 'dark');
      }
      if (key === 'sound-enabled') {
        setSoundEnabled(value === 'true');
      }
    };

    // Add event listener for our custom event
    window.addEventListener('settings-changed', handleSettingsChange as EventListener);

    // Clean up
    return () => {
      window.removeEventListener('settings-changed', handleSettingsChange as EventListener);
    };
  }, [setSoundEnabled]);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.classList.toggle('dark');
    const newTheme = newIsDark ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);

    // Dispatch a custom event
    window.dispatchEvent(
      new CustomEvent('settings-changed', {
        detail: { key: 'theme', value: newTheme }
      })
    );
  };

  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);

    // Dispatch a custom event
    window.dispatchEvent(
      new CustomEvent('settings-changed', {
        detail: { key: 'sound-enabled', value: String(newSoundEnabled) }
      })
    );
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  // Update the updateGameSection function to use the radio buttons
  const updateGameSection = (game: DailyPuzzleGame) => {
    const settings = {
      sudoku: { difficulty: sudokuDifficulty, setDifficulty: setSudokuDifficulty, stats: sudokuStats },
      '15puzzle': { difficulty: fifteenPuzzleDifficulty, setDifficulty: setFifteenPuzzleDifficulty, stats: fifteenPuzzleStats },
      '2048': { difficulty: 1, setDifficulty: null, stats: game2048Stats }
    }[game];
    const { difficulty, setDifficulty, stats } = settings;

    return (
      <div className="border-t pt-6 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t(game)}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {game === '2048' ? (
              <div className="space-y-3 text-gray-600 dark:text-gray-300">
                <p>{t('2048.automaticProgression')}</p>
                {record2048?.user === (currentUser?.uid ?? null) && record2048.value !== null && (
                  <p className="font-semibold text-gray-900 dark:text-white">{t('2048.personalRecord', { value: record2048.value })}</p>
                )}
                {recordError && <div role="alert">
                  <p>{t('2048.recordLoadFailed')}</p>
                  <button type="button" className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed rounded px-2 py-1 underline focus-ring" onClick={() => setRecordRetry(value => value + 1)}>{t('2048.retrySave')}</button>
                </div>}
              </div>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('profile.currentDifficulty')}
                </h3>
                <DifficultySelector
                  game={game}
                  currentDifficulty={difficulty}
                  onChange={(newDifficulty) => {
                    setDifficulty!(newDifficulty);
                    saveUserPreferredDifficulty(game, newDifficulty);
                  }}
                />
              </>
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('profile.completedPuzzles')}
            </h3>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>{t('easy')}: {stats.easy}</li>
              <li>{t('medium')}: {stats.medium}</li>
              <li>{t('hard')}: {stats.hard}</li>
              <li>{t('expert')}: {stats.expert}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-5 sm:py-8">
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          {t('profile')}
        </h1>

        {/* User Info */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-cobalt flex items-center justify-center text-white">
              {currentUser?.photoURL ? (
                <img src={currentUser.photoURL} alt="" className="h-16 w-16 rounded-full" />
              ) : (
                <span className="text-2xl">{(currentUser?.email || 'U')[0].toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 break-words">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {currentUser?.displayName || currentUser?.email?.split('@')[0] || t('brand.account')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">{currentUser?.email}</p>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('settings')}
          </h2>
          <div className="space-y-4">
            <button
              onClick={toggleSound}
              className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-2 text-gray-700 dark:text-gray-300"
            >
              {soundEnabled ? (
                <Volume2 className="h-5 w-5" />
              ) : (
                <VolumeX className="h-5 w-5" />
              )}
              <span>{soundEnabled ? t('soundOn') : t('soundOff')}</span>
            </button>

            <button
              onClick={toggleTheme}
              className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-2 text-gray-700 dark:text-gray-300"
            >
              {isDark ? (
                <>
                  <Moon className="h-5 w-5" />
                  <span>{t('darkMode')}</span>
                </>
              ) : (
                <>
                  <Sun className="h-5 w-5" />
                  <span>{t('lightMode')}</span>
                </>
              )}
            </button>

            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <select
                aria-label={t('brand.language')}
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="min-h-11 border border-slate-400 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded px-2 py-1"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        </div>

        {/* Game Preferences and Stats */}
        <div className="space-y-6">
          {/* Sudoku */}
          {updateGameSection('sudoku')}

          {/* 15 Puzzle */}
          {updateGameSection('15puzzle')}

          {/* 2048 */}
          {updateGameSection('2048')}
        </div>

        {/* Days Completed - with improved vertical alignment */}
        <div className="border-t pt-6 mt-8 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('daysCompleted', 'Days Completed')}
          </h2>
          <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Total Days */}
              <div className="flex flex-col justify-between items-center h-full">
                <div className="flex-grow flex items-center">
                  <div className="text-3xl font-bold tabular-nums text-cobalt dark:text-indigo-300">
                    {completedDays.total}
                  </div>
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                  {t('totalDaysCompleted', 'Total Days')}
                </div>
              </div>

              {/* Current Streak */}
              <div className="flex flex-col justify-between items-center h-full">
                <div className="flex-grow flex items-center">
                  <div className="text-3xl font-bold tabular-nums text-cobalt dark:text-indigo-300">
                    {completedDays.streak}
                  </div>
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                  {t('currentStreak', 'Current Streak')}
                </div>
              </div>

              {/* Last Completed */}
              <div className="flex flex-col justify-between items-center h-full">
                <div className="flex-grow flex items-center">
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">
                    {completedDays.lastCompleted ?
                      (() => {
                        // Parse date parts directly to avoid timezone issues completely
                        const [year, month, day] = completedDays.lastCompleted.split('-').map(Number);
                        // Create date using local time constructor (months are 0-indexed in JS)
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString(
                          i18n.language,
                          { month: 'short', day: 'numeric', year: 'numeric' }
                        );
                      })()
                    : '-'}
                  </div>
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                  {t('lastCompleted', 'Last Completed')}
                </div>
              </div>
            </div>

            <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('daysCompletedInfo')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
